/**
 * Decorative particle network for section backgrounds.
 *
 * Draws dots that drift and connect with hairlines when they come close. The
 * element sizes itself to its own box, so the calling CSS decides where the
 * field sits (usually `position: absolute; inset: 0` inside a hero).
 *
 * Purely decorative: the canvas is aria-hidden and the element is never focusable.
 *
 * @example
 * <particle-field class="hero__field" data-motion="true"></particle-field>
 */
class ParticleField extends HTMLElement {
  /** @type {HTMLCanvasElement | null} */
  #canvas = null;
  /** @type {CanvasRenderingContext2D | null} */
  #ctx = null;
  /** @type {{x: number, y: number, vx: number, vy: number, r: number, c: string}[]} */
  #points = [];
  /** @type {number | null} */
  #raf = null;
  #width = 1;
  #height = 1;
  /** @type {AbortController | null} */
  #controller = null;
  /** @type {ResizeObserver | null} */
  #resizeObserver = null;
  /** @type {IntersectionObserver | null} */
  #intersectionObserver = null;

  /** Movement is opt-out per section and always off when the OS asks for less motion. */
  get #animates() {
    if (this.dataset.motion === 'false') return false;
    return !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  get #linkDistance() {
    return Number(this.dataset.linkDistance) || 118;
  }

  connectedCallback() {
    // Reuse the canvas across reconnects — the theme morphs and moves nodes, and a
    // second append would stack a live canvas on top of a frozen one.
    const canvas = this.#canvas ?? document.createElement('canvas');
    canvas.setAttribute('aria-hidden', 'true');
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    if (!canvas.isConnected) this.append(canvas);
    this.#canvas = canvas;
    this.#ctx = ctx;

    // Fresh controller per connect: the previous one was aborted on disconnect and an
    // aborted signal makes addEventListener a no-op.
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.#resize();

    if ('ResizeObserver' in window) {
      this.#resizeObserver = new ResizeObserver(() => this.#resize());
      this.#resizeObserver.observe(this);
    } else {
      window.addEventListener('resize', () => this.#resize(), { signal });
    }

    // Burning frames on an off-screen canvas is pointless — pause while scrolled away.
    if ('IntersectionObserver' in window) {
      this.#intersectionObserver = new IntersectionObserver((entries) => {
        if (entries[0]?.isIntersecting) this.#start();
        else this.#stop();
      });
      this.#intersectionObserver.observe(this);
    } else {
      this.#start();
    }
  }

  disconnectedCallback() {
    this.#stop();
    this.#controller?.abort();
    this.#resizeObserver?.disconnect();
    this.#intersectionObserver?.disconnect();
    this.#resizeObserver = null;
    this.#intersectionObserver = null;
  }

  #resize() {
    const canvas = this.#canvas;
    const ctx = this.#ctx;
    if (!canvas || !ctx) return;

    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const rect = this.getBoundingClientRect();
    this.#width = Math.max(1, rect.width);
    this.#height = Math.max(1, rect.height);
    canvas.width = Math.round(this.#width * dpr);
    canvas.height = Math.round(this.#height * dpr);
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    this.#seed();

    // Keep points inside the new box instead of leaving a bare strip after a shrink.
    for (const point of this.#points) {
      point.x = Math.min(point.x, this.#width);
      point.y = Math.min(point.y, this.#height);
    }

    if (!this.#animates) this.#draw();
  }

  /** Point count follows the area, so a tall mobile hero does not get a dense mesh. */
  #seed() {
    const colors = ['rgba(54,55,81,', 'rgba(0,180,204,'];
    const target = Math.max(24, Math.min(60, Math.round((this.#width * this.#height) / 24000)));

    while (this.#points.length > target) this.#points.pop();
    while (this.#points.length < target) {
      this.#points.push({
        x: Math.random() * this.#width,
        y: Math.random() * this.#height,
        vx: -0.22 + Math.random() * 0.44,
        vy: -0.22 + Math.random() * 0.44,
        r: 1 + Math.random() * 1.6,
        c: colors[Math.random() < 0.5 ? 0 : 1] ?? colors[0],
      });
    }
  }

  #draw() {
    const ctx = this.#ctx;
    if (!ctx) return;

    const animates = this.#animates;
    const max = this.#linkDistance;

    ctx.clearRect(0, 0, this.#width, this.#height);

    for (let i = 0; i < this.#points.length; i++) {
      const a = this.#points[i];
      if (!a) continue;
      for (let j = i + 1; j < this.#points.length; j++) {
        const b = this.#points[j];
        if (!b) continue;
        const distance = Math.hypot(a.x - b.x, a.y - b.y);
        if (distance >= max) continue;
        ctx.strokeStyle = `rgba(77,217,236,${(0.16 * (1 - distance / max)).toFixed(3)})`;
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(a.x, a.y);
        ctx.lineTo(b.x, b.y);
        ctx.stroke();
      }
    }

    for (const point of this.#points) {
      if (animates) {
        point.x += point.vx;
        point.y += point.vy;
        if (point.x < -4) point.x = this.#width + 4;
        if (point.x > this.#width + 4) point.x = -4;
        if (point.y < -4) point.y = this.#height + 4;
        if (point.y > this.#height + 4) point.y = -4;
      }
      ctx.fillStyle = `${point.c}0.34)`;
      ctx.beginPath();
      ctx.arc(point.x, point.y, point.r, 0, 6.2832);
      ctx.fill();
    }
  }

  #frame = () => {
    this.#draw();
    this.#raf = requestAnimationFrame(this.#frame);
  };

  #start() {
    if (this.#raf != null || !this.#animates) return;
    this.#raf = requestAnimationFrame(this.#frame);
  }

  #stop() {
    if (this.#raf != null) cancelAnimationFrame(this.#raf);
    this.#raf = null;
  }
}

if (!customElements.get('particle-field')) {
  customElements.define('particle-field', ParticleField);
}
